import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Phone, MessageCircle, Gem, Award, Shield, Users } from "lucide-react";
import axios from "axios";
import ProductCard from "../components/ProductCard";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const LOGO_URL = "/images/logo.jpeg";

// Premium luxury jewellery hero images
const HERO_IMAGES = [
  "https://images.unsplash.com/photo-1601121141461-9d6647bca1ed?w=1920&q=85",
  "https://images.unsplash.com/photo-1515562141207-7a88fb7ce338?w=1920&q=85",
  "https://images.unsplash.com/photo-1611652022419-a9419f74343d?w=1920&q=85",
];

// Premium section images
const CRAFT_IMAGE = "/images/craft_img.jpeg";
const MODEL_IMAGE = "/images/model_image.jpeg";
const RETAIL_IMAGE = "/images/retail_img.jpeg";
const WHOLESALE_IMAGE = "/images/wholesale_img.jpeg";
const SHOWCASE_IMAGE_1 = "/images/showcase_img_1.jpeg";
const SHOWCASE_IMAGE_2 = "/images/showcase_img_2.jpeg";
const SHOWCASE_IMAGE_3 = "/images/showcase_img_3.jpeg";
const SHOWCASE_IMAGE_4 = "/images/showcase_img_4.jpeg";

const Home = () => {
  const [heroMedia, setHeroMedia] = useState([]);
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [currentHeroIndex, setCurrentHeroIndex] = useState(0);

  useEffect(() => {
    fetchHeroMedia();
    fetchFeaturedProducts();
  }, []);

  useEffect(() => {
    const images = heroMedia.length > 0 ? heroMedia : HERO_IMAGES;
    if (images.length > 1) {
      const interval = setInterval(() => {
        setCurrentHeroIndex((prev) => (prev + 1) % images.length);
      }, 5000);
      return () => clearInterval(interval);
    }
  }, [heroMedia]);

  const fetchHeroMedia = async () => {
    try {
      const response = await axios.get(`${API}/homepage-media?section=hero`);
      const data = response.data;
      console.log("HERO API RESPONSE →", response.data);

      if (Array.isArray(data)) {
        setHeroMedia(data);
      } else if (Array.isArray(data?.data)) {
        setHeroMedia(data.data);
      } else if (Array.isArray(data?.heroMedia)) {
        setHeroMedia(data.heroMedia);
      } else {
        console.warn("Hero media is not array:", data);
        setHeroMedia([]);
      }

    } catch (error) {
      console.error("Error fetching hero media:", error);
    }
  };

  const fetchFeaturedProducts = async () => {
    try {
      const response = await axios.get(`${API}/products?featured=true`);
      setFeaturedProducts(response.data.slice(0, 6));
    } catch (error) {
      console.error("Error fetching featured products:", error);
    }
  };

  const getMediaUrl = (mediaPath) => {
    if (!mediaPath) return HERO_IMAGES[0];
    if (mediaPath.startsWith("http")) return mediaPath;
    return `${BACKEND_URL}${mediaPath}`;
  };

  const safeHeroMedia = Array.isArray(heroMedia) ? heroMedia : [];

  const heroImages =
    safeHeroMedia.length > 0
      ? safeHeroMedia
      : HERO_IMAGES.map((url, i) => ({
        id: i,
        media_path: url,
        media_type: "image",
      }));

  const currentHero = heroImages[currentHeroIndex];


  return (
    <div data-testid="home-page" className="bg-ivory">
      {/* Hero Section - Premium Luxury */}
      <section className="relative min-h-screen flex items-center overflow-hidden pt-24">
        {/* Velvet texture overlay */}
        <div className="absolute inset-0 bg-gradient-to-br from-[#f8f5f0] via-[#f5f0e8] to-[#f0ebe0]" />
        <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E\")" }} />

        {/* Gold accent lines */}
        <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-royal-gold/30 to-transparent" />
        <div className="absolute bottom-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-royal-gold/30 to-transparent" />

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Left Content */}
            <div className="text-left space-y-6 lg:pr-8">
              <div className="flex items-center space-x-4">
                <div className="w-16 h-px bg-royal-gold" />
                <span className="font-display text-xs tracking-[0.3em] uppercase text-royal-gold">Est. Manekchowk, Ahmedabad</span>
              </div>

              <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-wide animate-fade-up" style={{ animationDelay: '0.2s' }}>
                <span className="text-gold-gradient">Shree Ganesh</span>
                <br />
                <span className="text-charcoal">Ornaments</span>
              </h2>

              <h1 className="font-serif text-xl sm:text-2xl text-charcoal/70 italic animate-fade-up" style={{ animationDelay: '0.4s' }}>
                Premium Silver Jewellery
              </h1>

              <p className="font-body text-charcoal/60 leading-relaxed max-w-md animate-fade-up" style={{ animationDelay: '0.5s' }}>
                Exquisite craftsmanship for <span className="text-royal-gold font-semibold">Retail</span> & <span className="text-royal-gold font-semibold">Wholesale</span> — Where tradition meets timeless elegance since generations.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 pt-4 animate-fade-up" style={{ animationDelay: '0.6s' }}>
                <Link to="/products" data-testid="hero-explore-btn" className="btn-luxury flex items-center justify-center space-x-3">
                  <span>View Collections</span><ArrowRight size={16} />
                </Link>
                <a href="https://wa.me/919104054259?text=Hello,%20I%20would%20like%20to%20enquire%20about%20your%20silver%20jewellery%20collection." target="_blank" rel="noopener noreferrer" data-testid="hero-whatsapp-btn" className="btn-luxury-outline flex items-center justify-center space-x-3">
                  <MessageCircle size={16} /><span>WhatsApp Us</span>
                </a>
              </div>
            </div>

            {/* Right - Hero Image with Frame */}
            <div className="relative animate-fade-up" style={{ animationDelay: '0.3s' }}>
              <div className="relative">
                {/* Decorative corners */}
                <div className="absolute -top-4 -left-4 w-20 h-20 border-t-2 border-l-2 border-royal-gold/50" />
                <div className="absolute -bottom-4 -right-4 w-20 h-20 border-b-2 border-r-2 border-royal-gold/50" />

                {/* Main hero image */}
                <div className="luxury-frame overflow-hidden">
                  {currentHero?.media_type === "video" ? (
                    <video
                      key={currentHeroIndex}
                      src={getMediaUrl(currentHero.media_path)}
                      className="w-full aspect-[4/5] object-cover animate-slow-zoom"
                      autoPlay
                      muted
                      loop
                      playsInline
                    />
                  ) : (
                    <img
                      key={currentHeroIndex}
                      src={getMediaUrl(currentHero.media_path)}
                      alt="Luxury Silver Jewellery"
                      className="w-full aspect-[4/5] object-cover animate-slow-zoom"
                    />
                  )}
                </div>


                {/* Gold shimmer overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-royal-gold/10 via-transparent to-transparent pointer-events-none" />
              </div>

              {/* Hero indicators */}
              {heroImages?.length > 1 && (
                <div className="flex justify-center space-x-2 mt-6">
                  {heroImages?.map((_, index) => (
                    <button key={index} onClick={() => setCurrentHeroIndex(index)}
                      className={`transition-all duration-500 h-1 ${index === currentHeroIndex ? "w-8 bg-royal-gold" : "w-3 bg-charcoal/20 hover:bg-charcoal/40"}`} />
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Showcase Gallery Section */}
      <section className="py-20 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-champagne-light/50 via-champagne/30 to-ivory" />
        <div className="absolute inset-0 opacity-[0.02]" style={{ backgroundImage: "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.8' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E\")" }} />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="col-span-2 row-span-2">
              <div className="luxury-frame h-full">
                <img src={MODEL_IMAGE} alt="Silver Jewellery Model" className="w-full h-full object-cover" />
              </div>
            </div>
            <div className="luxury-frame">
              <img src={SHOWCASE_IMAGE_1} alt="Silver Collection" className="w-full aspect-square object-cover" />
            </div>
            <div className="luxury-frame">
              <img src={SHOWCASE_IMAGE_2} alt="Silver Ornaments" className="w-full aspect-square object-cover" />
            </div>
            <div className="luxury-frame">
              <img src={SHOWCASE_IMAGE_3} alt="Premium Silver" className="w-full aspect-square object-cover" />
            </div>
            <div className="luxury-frame">
              <img src={SHOWCASE_IMAGE_4} alt="Craftsmanship" className="w-full aspect-square object-cover" />
            </div>
          </div>

          <div className="text-center mt-12">
            <p className="font-serif text-2xl text-charcoal/70 italic">
              "Crafting timeless elegance in pure silver"
            </p>
          </div>
        </div>
      </section>

      {/* Craftsmanship Section */}
      <section className="py-24 relative">
        <div className="absolute inset-0 bg-ivory" />
        <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-royal-gold/20 to-transparent" />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="relative">
              <div className="absolute -top-6 -left-6 w-24 h-24 border-t border-l border-royal-gold/30" />
              <div className="luxury-frame">
                <img src={CRAFT_IMAGE} alt="Silver Craftsmanship" className="w-full aspect-[4/5] object-cover" />
              </div>
              <div className="absolute -bottom-6 -right-6 w-24 h-24 border-b border-r border-royal-gold/30" />
            </div>

            <div className="space-y-8 lg:pl-8">
              <div className="flex items-center space-x-4">
                <div className="w-12 h-px bg-royal-gold" />
                <span className="font-display text-xs tracking-[0.4em] uppercase text-royal-gold">The Art of Silver</span>
              </div>

              <h2 className="font-display text-3xl sm:text-4xl text-charcoal">
                Masterful <span className="text-gold-gradient">Craftsmanship</span>
              </h2>

              <p className="font-body text-charcoal/60 leading-relaxed">
                Every piece from Shree Ganesh Ornaments is a testament to generations of silversmithing excellence. Our master artisans blend traditional techniques with contemporary designs to create jewellery that transcends time.
              </p>

              <ul className="space-y-4">
                {["925 Sterling Silver Quality", "Handcrafted by Master Artisans", "Traditional & Modern Designs", "Certified Purity Guarantee"].map((item, i) => (
                  <li key={i} className="flex items-center space-x-4">
                    <span className="w-8 h-px bg-royal-gold" />
                    <span className="font-body text-charcoal/70">{item}</span>
                  </li>
                ))}
              </ul>

              <Link to="/about" className="inline-flex items-center space-x-2 font-display text-xs tracking-[0.2em] uppercase text-royal-gold hover:text-royal-gold/70 transition-colors">
                <span>Our Heritage</span><ArrowRight size={14} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Retail & Wholesale Section */}
      <section className="py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-pearl via-cream to-champagne-light/50" />
        <div className="absolute inset-0 opacity-[0.02]" style={{ backgroundImage: "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E\")" }} />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-16">
            <span className="font-display text-xs tracking-[0.4em] uppercase text-royal-gold">Serving All</span>
            <h2 className="font-display text-3xl sm:text-4xl text-charcoal mt-4">
              Retail & <span className="text-gold-gradient">Wholesale</span>
            </h2>
            <div className="w-24 h-px bg-gradient-to-r from-transparent via-royal-gold to-transparent mx-auto mt-6" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
            <div className="group">
              <div className="relative overflow-hidden">
                <div className="absolute -top-3 -left-3 w-16 h-16 border-t border-l border-royal-gold/40 z-10" />
                <div className="luxury-frame">
                  <img src={RETAIL_IMAGE} alt="Retail Collection" className="w-full aspect-[4/3] object-cover group-hover:scale-105 transition-transform duration-1000" />
                </div>
                <div className="absolute -bottom-3 -right-3 w-16 h-16 border-b border-r border-royal-gold/40" />
              </div>
              <div className="mt-8 text-center">
                <span className="font-display text-xs tracking-[0.3em] uppercase text-royal-gold">For Collectors</span>
                <h3 className="font-serif text-2xl text-charcoal mt-2 mb-3">Retail Collection</h3>
                <p className="font-body text-charcoal/60 mb-4">Discover exclusive pieces for personal adornment. Each ornament tells a story of elegance.</p>
                <Link to="/products" className="font-display text-xs tracking-widest uppercase text-royal-gold hover:underline">Explore Retail →</Link>
              </div>
            </div>

            <div className="group">
              <div className="relative overflow-hidden">
                <div className="absolute -top-3 -left-3 w-16 h-16 border-t border-l border-silver/50 z-10" />
                <div className="luxury-frame">
                  <img src={WHOLESALE_IMAGE} alt="Wholesale Selection" className="w-full aspect-[4/3] object-cover group-hover:scale-105 transition-transform duration-1000" />
                </div>
                <div className="absolute -bottom-3 -right-3 w-16 h-16 border-b border-r border-silver/50" />
              </div>
              <div className="mt-8 text-center">
                <span className="font-display text-xs tracking-[0.3em] uppercase text-silver-dark">For Trade</span>
                <h3 className="font-serif text-2xl text-charcoal mt-2 mb-3">Wholesale Selection</h3>
                <p className="font-body text-charcoal/60 mb-4">Partner with us for quality and reliability. Competitive pricing for traders.</p>
                <Link to="/contact" className="font-display text-xs tracking-widest uppercase text-royal-gold hover:underline">Enquire Wholesale →</Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-24 bg-ivory relative">
        <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-royal-gold/20 to-transparent" />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: Gem, title: "Pure Silver", desc: "925 Sterling certified quality" },
              { icon: Award, title: "Master Craft", desc: "Generations of expertise" },
              { icon: Shield, title: "Trust", desc: "Transparent & honest dealings" },
              { icon: Users, title: "Service", desc: "Retail & wholesale support" },
            ].map((item, index) => (
              <div key={index} className="text-center p-8 bg-white border border-gold-muted/10 hover:border-royal-gold/30 hover:shadow-luxury transition-all duration-500 group">
                <div className="w-16 h-16 mx-auto mb-6 border border-royal-gold/30 flex items-center justify-center group-hover:bg-royal-gold/5 transition-colors">
                  <item.icon size={28} className="text-royal-gold" />
                </div>
                <h3 className="font-display text-sm tracking-[0.2em] uppercase text-charcoal mb-2">{item.title}</h3>
                <p className="font-body text-sm text-charcoal/50">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Products */}
      {featuredProducts.length > 0 && (
        <section className="py-24 relative overflow-hidden" data-testid="featured-products-section">
          <div className="absolute inset-0 bg-gradient-to-b from-cream via-champagne-light/30 to-ivory" />

          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <div className="text-center mb-16">
              <span className="font-display text-xs tracking-[0.4em] uppercase text-royal-gold">Curated Selection</span>
              <h2 className="font-display text-3xl sm:text-4xl text-charcoal mt-4">
                Featured <span className="text-gold-gradient">Collections</span>
              </h2>
              <div className="w-24 h-px bg-gradient-to-r from-transparent via-royal-gold to-transparent mx-auto mt-6" />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-10">
              {featuredProducts.map((product, index) => (
                <ProductCard key={product.id} product={product} index={index} />
              ))}
            </div>

            <div className="text-center mt-12">
              <Link to="/products" data-testid="view-all-products-btn" className="btn-luxury-outline inline-flex items-center space-x-3">
                <span>View All Collections</span><ArrowRight size={16} />
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="py-24 bg-gradient-to-r from-royal-gold via-gold-light to-royal-gold relative overflow-hidden">
        <div className="absolute inset-0 opacity-10" style={{ backgroundImage: "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E\")" }} />

        <div className="max-w-4xl mx-auto px-4 text-center relative z-10">
          <span className="font-display text-xs tracking-[0.4em] uppercase text-white/80">Begin Your Journey</span>
          <h2 className="font-display text-3xl sm:text-4xl text-white mt-4 mb-6">Experience Excellence</h2>
          <p className="font-body text-white/80 mb-10 max-w-xl mx-auto">
            Visit our showroom in Manekchowk or connect with us for retail purchases and wholesale partnerships.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a href="tel:+919104054259" data-testid="cta-call-btn" className="bg-white text-royal-gold font-display tracking-[0.2em] uppercase px-10 py-4 text-sm hover:bg-ivory transition-colors flex items-center space-x-3 shadow-lg">
              <Phone size={16} /><span>+91 9104054259</span>
            </a>
            <Link to="/contact" className="border-2 border-white text-white font-display tracking-[0.2em] uppercase px-10 py-4 text-sm hover:bg-white/10 transition-colors">
              Bulk Enquiry
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
